import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react"
import { createPortal } from "react-dom"
import { normalizeColor, opacityOf, opaque, sameColor, TV_PALETTE, withOpacity } from "./color.ts"

// TradingView's colour picker: the colour grid, your custom colours, opacity,
// and (for lines) thickness and line style.

export type PickerLineStyle = "solid" | "dashed" | "dotted"
export type PickerLineWidth = 1 | 2 | 3 | 4

const WIDTHS: ReadonlyArray<PickerLineWidth> = [1, 2, 3, 4]
const STYLES: ReadonlyArray<PickerLineStyle> = ["solid", "dashed", "dotted"]

// --- Custom colours: this browser's own, as in TradingView -------------------------

const CUSTOM_KEY = "trader.customColors.v1"
const MAX_CUSTOM = 20

const loadCustom = (): ReadonlyArray<string> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(CUSTOM_KEY) ?? "[]")
    return Array.isArray(parsed)
      ? parsed.flatMap((value) => (typeof value === "string" ? normalizeColor(value) ?? [] : [])).slice(0, MAX_CUSTOM)
      : []
  } catch {
    return []
  }
}

let customColors = loadCustom()
const customListeners = new Set<() => void>()
const addCustomColor = (color: string) => {
  customColors = [opaque(color), ...customColors.filter((c) => !sameColor(c, color, { ignoreAlpha: true }))].slice(0, MAX_CUSTOM)
  try {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(customColors))
  } catch {
    // Kept for this page only.
  }
  for (const listener of customListeners) listener()
}
const subscribeCustom = (listener: () => void) => {
  customListeners.add(listener)
  return () => customListeners.delete(listener)
}

export const LineSample = ({ lineStyle = "solid", width }: { readonly width: number; readonly lineStyle?: PickerLineStyle | undefined }) => (
  <svg width="28" height="10" aria-hidden>
    <line
      x1="2"
      y1="5"
      x2="26"
      y2="5"
      stroke="currentColor"
      strokeWidth={width}
      strokeDasharray={lineStyle === "dashed" ? "6 3" : lineStyle === "dotted" ? "1.5 2.5" : undefined}
      strokeLinecap="round"
    />
  </svg>
)

interface PickerProps {
  readonly color: string
  readonly onColor: (color: string) => void
  /** Shown when given: thickness of a line. */
  readonly width?: PickerLineWidth | undefined
  readonly onWidth?: ((width: PickerLineWidth) => void) | undefined
  /** Shown when given: solid, dashed, or dotted. */
  readonly lineStyle?: PickerLineStyle | undefined
  readonly onLineStyle?: ((style: PickerLineStyle) => void) | undefined
}

/** The picker's body. Picking a grid colour keeps the current opacity. */
export function ColorPicker({ color, lineStyle, onColor, onLineStyle, onWidth, width }: PickerProps) {
  const custom = useSyncExternalStore(subscribeCustom, () => customColors)
  const nativeRef = useRef<HTMLInputElement>(null)
  const opacity = opacityOf(color)
  const pick = (picked: string) => onColor(withOpacity(picked, opacity))
  // React's onChange fires on every step of a drag in the system colour chooser; the
  // custom colour is kept once, when the chooser commits (the native "change" event).
  useEffect(() => {
    const input = nativeRef.current
    if (input === null) return
    const onCommit = () => {
      const picked = normalizeColor(input.value)
      if (picked !== null) addCustomColor(picked)
    }
    input.addEventListener("change", onCommit)
    return () => input.removeEventListener("change", onCommit)
  }, [])
  const swatch = (value: string) => (
    <button
      key={value}
      type="button"
      aria-label={value}
      aria-pressed={sameColor(value, color, { ignoreAlpha: true })}
      style={{ background: value }}
      onClick={() => pick(value)}
    />
  )
  return (
    <div className="color-picker">
      <div className="color-grid">{TV_PALETTE.flat().map(swatch)}</div>
      <div className="color-custom">
        {custom.map(swatch)}
        <button type="button" className="color-add" title="Add a custom colour" onClick={() => nativeRef.current?.click()}>+</button>
        <input
          ref={nativeRef}
          type="color"
          className="visually-hidden"
          tabIndex={-1}
          value={opaque(color).slice(0, 7).toLowerCase()}
          onChange={(event) => {
            const picked = normalizeColor(event.target.value)
            if (picked !== null) pick(picked)
          }}
        />
      </div>
      <label className="color-opacity">
        <span>Opacity</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(opacity * 100)}
          onChange={(event) => onColor(withOpacity(color, Number(event.target.value) / 100))}
        />
        <span className="color-opacity-value">{Math.round(opacity * 100)}%</span>
      </label>
      {onWidth && (
        <div className="picker-row" role="group" aria-label="Thickness">
          {WIDTHS.map((w) => (
            <button key={w} type="button" aria-pressed={width === w} title={`${w}px`} onClick={() => onWidth(w)}>
              <LineSample width={w} />
            </button>
          ))}
        </div>
      )}
      {onLineStyle && (
        <div className="picker-row" role="group" aria-label="Line style">
          {STYLES.map((s) => (
            <button key={s} type="button" aria-pressed={lineStyle === s} title={s} onClick={() => onLineStyle(s)}>
              <LineSample width={2} lineStyle={s} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * A colour swatch (with a line sample when it has a thickness) that opens the
 * picker in a popover. The popover is fixed-positioned in a portal, so dialogs'
 * scroll areas don't clip it; it closes on an outside press or Escape (without
 * closing the dialog behind it).
 */
export function ColorSwatchButton({ title, ...picker }: PickerProps & { readonly title: string }) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<CSSProperties>({ visibility: "hidden" })

  useLayoutEffect(() => {
    if (!open) return
    const anchor = anchorRef.current?.getBoundingClientRect()
    const popover = popoverRef.current?.getBoundingClientRect()
    if (anchor === undefined || popover === undefined) return
    const margin = 8
    const below = anchor.bottom + 4
    const top = below + popover.height > window.innerHeight - margin ? Math.max(margin, anchor.top - 4 - popover.height) : below
    const left = Math.min(Math.max(margin, anchor.left), window.innerWidth - popover.width - margin)
    setPosition({ top, left })
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    // Capture on window: runs before the dialog's own Escape listener, which it stops.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    window.addEventListener("keydown", onKey, true)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      window.removeEventListener("keydown", onKey, true)
    }
  }, [open])

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="swatch-button"
        title={title}
        aria-label={title}
        aria-expanded={open}
        onClick={() => {
          setPosition({ visibility: "hidden" })
          setOpen(!open)
        }}
      >
        <span className="swatch-checker">
          <span className="swatch-fill" style={{ background: picker.color }} />
        </span>
        {picker.width !== undefined && (
          <span className="swatch-line" style={{ color: opaque(picker.color) }}>
            <LineSample width={picker.width} lineStyle={picker.lineStyle} />
          </span>
        )}
      </button>
      {open && createPortal(
        <div ref={popoverRef} className="picker-popover" style={{ position: "fixed", ...position }}>
          <ColorPicker {...picker} />
        </div>,
        document.body
      )}
    </>
  )
}
